import React, { useState, useEffect, useContext } from 'react';
import { GlobalContext, useGlobalContext } from '../../context/GlobalContext';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { usePayment } from '../../context/PaymentContext';
import { useCartContext } from '../../context/CartContext';
import './Payment.css';
import { formatDateTime } from '../../utils/DateUtils';

const PaymentResult = () => {
  const { t } = useTranslation();
  const { goToStep } = useContext(GlobalContext);
  const { formatAmounts, store, step } = useGlobalContext();
  const { setCart } = useCartContext();
  const { checkTransaction } = usePayment();
  const { status, reference } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [order, setOrder] = useState({});
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (loading == false && ready) {
      if (step == 1 || step == 2) {
        navigate("/")
      }
    }
  }, [step]);

  useEffect(() => {
    const verifyTransaction = async () => {
      let transactionReference = reference;

      goToStep(3);
      try {
        setLoading(true);
        const storedOrderData = sessionStorage.getItem('orderData');
        const storedOrder = storedOrderData ? JSON.parse(storedOrderData).order : null;
        const queryParams = new URLSearchParams(location.search);

        if (storedOrder && storedOrder.ref) {
          transactionReference = storedOrder.ref;
        } else if (!transactionReference) {
          if (queryParams.has('response_order_id')) {
            transactionReference = queryParams.get('response_order_id');
          } else {
            const possibleRefParams = ['orderID', 'ref', 'ORDERID', 'id'];
            for (const paramName of possibleRefParams) {
              if (queryParams.has(paramName)) {
                transactionReference = queryParams.get(paramName);
                break;
              }
            }
          }
        }

        if (status !== 'canceled') {
          const result = await checkTransaction(
            transactionReference,
            status,
            Object.fromEntries(queryParams.entries())
          );

          if (result.status === 'error') {
            setOrder({ status: 'exception', exception: result.errors });
          } else if (result.cards) {
            sessionStorage.removeItem('totalAmounts');
            sessionStorage.removeItem('cart');
            setCart({ items: [] });

            setOrder({
              ...result,
              cards: result.cards,
              status: status === "result" ? result.cards[0].status : status,
              store: result.cards[0].physical_cart_group ? 'physical' : 'digital'
            });
          } else {
            sessionStorage.removeItem('totalAmounts');
            sessionStorage.removeItem('cart');
            setCart({ items: [] });
            goToStep(1);
            navigate("/");
            return;
          }
        } else {
          setOrder({ status: 'canceled' });
          const cartData = sessionStorage.getItem('cart');
          if (cartData) {
            setCart(JSON.parse(cartData));
          } else {
            setCart({ items: [] });
          }
        }
      } catch (error) {
        console.log('verifyTransaction : exception', error);
        setOrder({ status: 'exception', exception: [{ message: error.message }] });
      } finally {
        setLoading(false);
        setTimeout(() => setReady(true), 1200);
      }
    };

    verifyTransaction();
  }, [status, reference, location.search]);

  const goPanier = (e) => {
    e.preventDefault();
    goToStep(2);
    setTimeout(() => {
      window.location.href = "/home"
    }, 500);
  };

  const goToHomeAndSave = (e) => {
    e.preventDefault();
    goToStep(1);
    setTimeout(() => {
      window.location.href = "/home"
    }, 500);
  };

  if (loading) {
    return (
      <div id="preloader">
        <div id="loader"></div>
      </div>
    );
  }

  if (order?.status !== 'authorized') {
    return (
      <>
        <div id="topSection" className="ng-scope" role="region">
          <div className={`overlayBox ${store.mainImageFilter ? 'overlay' : ''}`}>
          </div>
        </div>
        <main id="main-content" tabIndex="-1" className="mainContent step-result">
          <div className="wrapper mainWhiteBox">
            <div className="contentBody mainColor typoBold">
              <div className="confirmation errorsItems">
                {status === 'refused' && <div className="error">{t('Votre paiement a été refusé')}</div>}
                {status === 'canceled' && <div className="error">{t('Vous avez annulé votre paiement')}</div>}
                {status === 'incomplete' && (
                  <div className="error">
                    {t("Votre paiement n'a pas pu aboutir.")}
                    <br />
                    {t('Veuillez réessayer')}
                  </div>
                )}
                {status === 'unpayed' && <div className="error">{t('Paiement non effectué')}</div>}
                {order?.status === 'exception' && order.exception?.map((err, index) => (
                  <div key={index} className="error">{err.message}</div>
                ))}
              </div>
              <div className="toHomeAction">
                <button onClick={goPanier} className="primaryButton btn">
                  {t('Retour à ma commande')}
                </button>
              </div>
            </div>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <div id="topSection" className="ng-scope" role="region" aria-labelledby="confirmation-message">
        <div className={`overlayBox ${store.mainImageFilter ? 'overlay' : ''}`}>
          {order?.status === 'authorized' && (
            <div className="wrapper">
              <h1 id="confirmation-message" className="headContent">
                {t('Votre commande a été enregistrée avec succès ! Gardez précieusement ces informations.')}
              </h1>
            </div>
          )}
        </div>
      </div>
      <main id="main-content" tabIndex="-1" className="mainContent step-result">
        {order?.status === 'authorized' && ready && order?.cards && order.cards.length > 0 && (
          <div className="wrapper authorized ng-scope">
            <div className="orderConfirmation typo">
              <section id="cards-section" tabIndex="-1" className="orderDetails" aria-labelledby="cards-section-title">
                <h2 id="cards-section-title" className="sr-only">{t('Vos cartes cadeaux')}</h2>
                {order.cards.map((card, index) => (
                  <React.Fragment key={index}>
                    <div className="card-item">
                      {renderCardContent(card, index)}
                    </div>
                    {index < order.cards.length - 1 && (
                      <div
                        className="card-separator"
                        style={{
                          height: '1px',
                          backgroundColor: '#0000001A',
                          marginLeft: "32px",
                          marginRight: "32px",
                          marginTop: "16px",
                          justifyContent: "center"
                        }}
                      />
                    )}
                  </React.Fragment>
                ))}
              </section>

              <section id="confirmation-section" tabIndex="-1" className="confirmationBox mainWhiteBox" aria-labelledby="confirmation-title">
                <div className="confirmationContent">
                  <div className="confirmationHeader">
                    <h2 id="confirmation-title" className="confirmationTitle typoBold mainColor">
                      {order.cards[0]?.firstNameSender}{' '}{order.cards[0]?.lastNameSender}
                      <span className="cardSubtitle typo">
                        {order.cards[0]?.emailSender}
                      </span>
                    </h2>
                    <p className="confirmationMessage font">
                      {order.store === 'digital' && (
                        <>
                          {t('Nous avons bien reçu votre commande référence')} <span className="typoBold mainColor">{order.cards[0]?.reference}</span> {t("pour une e-carte cadeau d'une valeur de")} <span className="typoBold mainColor">{formatAmounts(order.cards[0]?.amountAfterDiscount, order.cards[0]?.amountAfterDiscount % 1 ? 2 : 0)}</span>, {t('votre paiement est en cours de validation.')}<br />
                        </>
                      )}
                      {order.store === 'physical' && (
                        <>
                          {t('confirmation.physical.first_paragraph.before_reference')} <span className="typoBold mainColor">{order.cards[0]?.reference}</span> {t('confirmation.physical.between_reference_amount')} <span className="typoBold mainColor">{formatAmounts(order.cards[0]?.amountAfterDiscount)}</span>{t('confirmation.physical.after_amount')}<br />
                        </>
                      )}
                      {t('Un mail de confirmation sera envoyé à votre adresse email')}{' '}
                      <span className="typoBold mainColor">{order.cards[0]?.emailSender}</span>.
  <br /><br />
                      {t("Votre numéro de transaction est le")}{' '}
                      <span className="typoBold mainColor">{order.cards[0]?.idTransaction}</span>
                      <br />
                      {t("il vous sera rappelé dans l'email de confirmation")}
                      <br /><br />
                      <span className="typoBold mainColor">{order.cards[0]?.signboardName}</span>{' '}
                      {t('vous remercie de votre confiance.')}
                    </p>

                    {order.store === 'digital' && store?.video_enabled && order.cards[0]?.videoCode && (
                      <p className="videoMessage typoMedium font">
                        {t('Cliquez sur ce lien pour ajouter une vidéo à cette carte')}{' '}
                        <a
                          href={`${store?.video_url_buyer || '#'}${order.cards[0]?.videoCode}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-video mainColor"
                          aria-label={`${t('Ajouter une vidéo')} (${t('S\'ouvre dans un nouvel onglet')})`}
                          onClick={(e) => {
                            if (!store?.video_url_buyer) {
                              e.preventDefault();
                              console.log('URL vidéo non disponible');
                            }
                          }}
                        >
                          {t('Ajouter une vidéo')}
                          <span className="sr-only"> ({t('S\'ouvre dans un nouvel onglet')})</span>
                        </a>
                      </p>
                    )}
                  </div>
                  <div id="actions-section" tabIndex="-1" className="actionButtons" aria-labelledby="actions-section-title">
                    <h3 id="actions-section-title" className="sr-only">{t('Actions disponibles')}</h3>
                    <button
                      className="btn buyOther typo"
                      onClick={goToHomeAndSave}
                    >
                      {t('Acheter une autre carte')}
                    </button>
                  </div>
                </div>
              </section>
            </div>
          </div>
        )}
      </main>
    </>
  );

  function renderCardContent(card, index) {
    const cardContainerStyle = index > 0 ? {
      paddingTop: "16px",
    } : {};

    return (
      <article className="payment-card-container" style={cardContainerStyle}>
        <div className="payment-card-upper">
          <div className="payment-card-image-container">
            <img
              loading="lazy"
              src={card.card?.src}
              alt={`${t('Aperçu de la carte cadeau')} ${card.signboardName}`}
              className="payment-card-image"
            />
          </div>
          <div className="payment-card-info">
            <div className="payment-card-header">
              <div>
                <h3 className="payment-card-title typoMedium mainColor">
                  {t('Carte Cadeau')}
                </h3>
                <p className="payment-card-subtitle mainColor">
                  {card.signboardName}
                </p>
              </div>
              <div className="payment-card-value typoBold mainColor">
                {formatAmounts(card.amountAfterDiscount)}
              </div>
            </div>
            <div className="payment-card-delivery-info font">
              {order.store === 'digital' && card.emailReceiver !== card.emailSender && card.sentDate && (
                <>
                  {t('La e-carte sera envoyée par email le :')} {' '}
                  {moment(card.sentDate.date, 'DD/MM/YYYY').format('DD/MM/YYYY')} {' '}
                 {/*  {t('à')} {' '}
                  <span className="time-display">
                    {(() => {
                      const timeData = formatDateTime.formatDisplayTimeWithTimezone(card.sentDate.time, store.format_time);
                      return (
                        <>
                          {timeData.time}
                          <span className="timezone-small">{timeData.timezone}</span>
                        </>
                      );
                    })()}
                  </span> */}
                </>
              )}
              {order.store === 'digital' && card.emailReceiver === card.emailSender && (
                t('Remise en main propre')
              )}
              {order.store === 'physical' && (
                t('Envoyée prochainement')
              )}
            </div>
          </div>
        </div>

        <div className="payment-card-footer">
          <div className="payment-card-info-horizontal" style={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            marginBottom: '15px'
          }}>
            <div className="payment-card-beneficiary" style={{ flex: '1' }}>
              <h4 className="payment-card-beneficiary-title typoBold mainColor">
                {t('Destinataire')}
              </h4>
              <div>
                <p className="payment-card-beneficiary-name typoMedium">
                  {card.firstNameReceiver} {card.lastNameReceiver}
                </p>
                <p className="payment-card-beneficiary-email">
                  {card.emailReceiver}
                </p>
              </div>
            </div>

            {card.message && (
              <div className="payment-card-message" style={{ flex: '1' }}>
                <h4 className="payment-card-message-title typoBold mainColor">
                  {t('Voici votre message :')}
                </h4>
                <p className="payment-card-message-content font">
                  <span className="msgPreview">"{card.message}"</span>
                </p>
              </div>
            )}
          </div>

          <div className="download-button-container" style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-start'
          }}>
            {card.dl && (
              <a
                href={card.dl}
                download={`carte-cadeau-${card.signboardName.replace(/\s+/g, '-').toLowerCase()}.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  minWidth: '210px',
                  maxWidth: '230px',
                  height: '40px',
                  borderRadius: '50px',
                  paddingRight: '16px',
                  paddingLeft: '16px',
                  gap: '9px',
                  marginTop: '16px',
                  justifyContent: 'center',
                  fontSize: "14px",
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center'
                }}
                className="btn sendOrder"
                aria-label={`${t('Télécharger ma carte')} ${card.signboardName} (${t('S\'ouvre dans un nouvel onglet')})`}
                onClick={(e) => {
                  if (!card.dl) {
                    e.preventDefault();
                    console.log('Lien de téléchargement non disponible');
                  }
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  role="presentation"
                  aria-hidden="true"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                {t('Télécharger ma carte')}
                <span className="sr-only"> ({t('S\'ouvre dans un nouvel onglet')})</span>
              </a>
            )}
          </div>
        </div>
      </article>
    );
  }
}

export default PaymentResult;